import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import Header from '../components/Header/header'
import SubscribeSection from '../components/Subscribe'
import FooterSection from '../components/Footer'
import firebase from '../api/firestore'

const db = firebase.firestore()

export default function UserPage({ match }) {
  const { uid } = match.params
  const [currentUser, setCurrentUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [items, setItems] = useState([])
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((u) => setCurrentUser(u))
    return unsubscribe
  }, [])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [profileDoc, snapshot] = await Promise.all([
          db.collection('users').doc(uid).get(),
          db.collection('itineraries').where('submittedBy.uid', '==', uid).get(),
        ])

        if (profileDoc.exists) setProfile(profileDoc.data())

        const allDocs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        const nameDoc = allDocs.find((d) => d.submittedBy?.displayName)
        if (nameDoc) setDisplayName(nameDoc.submittedBy.displayName)

        const approved = allDocs
          .filter((doc) => doc.approved)
          .sort((a, b) => (b.createdAt?.toDate() || 0) - (a.createdAt?.toDate() || 0))
        setItems(approved)
      } catch (err) {
        console.error('Error fetching user data:', err)
      }
      setLoading(false)
    }
    fetchAll()
  }, [uid])

  const isOwner = currentUser && currentUser.uid === uid
  const name = profile?.displayName || displayName || 'Community Member'


  return (
    <>
      <Helmet>
        <title>{name !== 'Community Member' ? `${name}'s Profile` : 'Traveler Profile'} | ExplorOFF</title>
      </Helmet>

      <Header />

      <div className="exploroff-submit-page">
        <div className="container">

          {/* Profile header card */}
          <div className="exploroff-submit-card exploroff-profile-header-card">
            <div className="exploroff-profile-top">
              <div className="exploroff-profile-avatar-wrap">
                <div className="exploroff-profile-avatar-lg">
                  {name[0].toUpperCase()}
                </div>
              </div>

              <div className="exploroff-profile-name-block">
                <h1 className="exploroff-submit-title exploroff-profile-name">
                  {name}
                  {profile?.isAgent && (
                    <span className="exploroff-agent-badge">Travel Pro</span>
                  )}
                </h1>
                {profile?.bio && (
                  <p className="exploroff-profile-bio">{profile.bio}</p>
                )}
              </div>
              {isOwner && (
                <Link to="/profile/edit" className="exploroff-edit-profile-btn">
                  Edit profile
                </Link>
              )}
            </div>

            {/* Travel Q&A */}
            {(profile?.favoriteDestination || profile?.dreamDestination || profile?.travelStyle || profile?.favoriteActivity) && (
              <div className="exploroff-profile-qa">
                {profile.favoriteDestination && (
                  <div className="exploroff-profile-qa-item">
                    <span className="exploroff-profile-qa-q">Favorite destination</span>
                    <span className="exploroff-profile-qa-a">{profile.favoriteDestination}</span>
                  </div>
                )}
                {profile.dreamDestination && (
                  <div className="exploroff-profile-qa-item">
                    <span className="exploroff-profile-qa-q">Dream destination</span>
                    <span className="exploroff-profile-qa-a">{profile.dreamDestination}</span>
                  </div>
                )}
                {profile.favoriteActivity && (
                  <div className="exploroff-profile-qa-item">
                    <span className="exploroff-profile-qa-q">Favorite activity</span>
                    <span className="exploroff-profile-qa-a">{profile.favoriteActivity}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Travel agent card */}
          {profile?.isAgent && (
            <div className="exploroff-agent-card">
              <div className="exploroff-agent-card-header">
                <div>
                  <h2 className="exploroff-agent-card-name">{profile.businessName || name}</h2>
                  {profile.businessTagline && (
                    <p className="exploroff-agent-card-tagline">"{profile.businessTagline}"</p>
                  )}
                  {profile.businessSpecialty && (
                    <p className="exploroff-agent-card-specialty">Specialty: {profile.businessSpecialty}</p>
                  )}
                </div>
              </div>
              <div className="exploroff-agent-card-contacts">
                {profile.businessWebsite && (
                  <a
                    href={profile.businessWebsite}
                    target="_blank"
                    rel="noreferrer"
                    className="exploroff-agent-contact-link"
                  >
                    🌐 Learn More
                  </a>
                )}
                {profile.businessPhone && (
                  <a
                    href={`https://instagram.com/${profile.businessPhone.replace(/^@/, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="exploroff-agent-contact-link"
                  >
                    <svg className="exploroff-ig-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.975-.975 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.333.014 7.053.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.053.014 8.333 0 8.741 0 12c0 3.259.014 3.667.072 4.947.085 1.856.601 3.698 1.942 5.039 1.341 1.341 3.183 1.857 5.039 1.942C8.333 23.986 8.741 24 12 24c3.259 0 3.667-.014 4.947-.072 1.856-.085 3.698-.601 5.039-1.942 1.341-1.341 1.857-3.183 1.942-5.039.058-1.28.072-1.688.072-4.947 0-3.259-.014-3.667-.072-4.947-.085-1.856-.601-3.698-1.942-5.039C20.645.673 18.803.157 16.947.072 15.667.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
                    </svg>
                    Follow me
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Itineraries */}
          <div className="exploroff-feed-section">
            <h2 className="exploroff-feed-title">
              {isOwner ? 'My itineraries' : `${name}'s itineraries`}
            </h2>

            {loading ? (
              <p className="exploroff-feed-loading">Loading...</p>
            ) : items.length === 0 ? (
              <p className="exploroff-feed-loading">
                No approved itineraries yet.{isOwner ? (
                  <> <Link to="/submit-itinerary">Submit one!</Link></>
                ) : ' Submissions appear here once reviewed.'}
              </p>
            ) : (
              <div className="exploroff-feed-grid">
                {items.map((item) => (
                  <div key={item.id} className="exploroff-feed-card">
                    <div className="exploroff-feed-card-header">
                      <div className="exploroff-feed-card-main">
                        <Link to={`/itinerary/${item.id}`} className="exploroff-feed-card-title-link">
                          <h3 className="exploroff-feed-card-title">{item.name || 'Itinerary'}</h3>
                        </Link>
                        <p className="exploroff-feed-card-date">
                          {item.createdAt
                            ? new Date(item.createdAt.toDate()).toLocaleDateString()
                            : ''}
                        </p>
                      </div>
                      <button type="button" className="exploroff-feed-vote-button">
                        👍 {item.voteCount || 0}
                      </button>
                    </div>
                    {item.description && (
                      <p className="exploroff-feed-card-description">{item.description}</p>
                    )}
                    <a
                      href={item.googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="exploroff-feed-map-link"
                    >
                      Open Map
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="itinerary-page-back" style={{ marginTop: 32 }}>
            <Link to="/submit-itinerary">← Back to Community Itineraries</Link>
          </p>
        </div>
      </div>

      <SubscribeSection />
      <FooterSection />
    </>
  )
}
